import React from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {categories,categoryDescriptions} from '../data/quizData';
import ScoreChart from './ScoreChart';

const {
  FiAward,
  FiHeart,
  FiCalendar,
  FiRefreshCw,
  FiMail,
  FiArrowRight
} = FiIcons;

const BOOKING_URL='https://tidycal.com/markdee/free-talk';

const emailLessons=[
  'Why this pattern started—and why it made sense back then.',
  'How it shows up in your life right now.',
  'The story that keeps it running.',
  'What it costs you and the people you love.',
  'What to do instead,so it stops running the show.'
];

const ResultsPage=({participantData,quizResults})=> {
  const navigate=useNavigate();

  if (!participantData.name || !quizResults) {
    navigate('/');
    return null;
  }

  const {topThree,allScores}=quizResults;

  const handleBookCall=()=> {
    window.open(BOOKING_URL,'_blank','noopener,noreferrer');
  };

  const handleRetakeQuiz=()=> {
    navigate('/');
  };

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      transition={{duration: 0.6}}
      className="min-h-screen p-2 sm:p-4"
    >
      <div className="max-w-4xl mx-auto">
        <motion.section
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.2,duration: 0.6}}
          className="bg-gradient-to-br from-midnight-blue via-royal-blue to-md-blue rounded-2xl shadow-2xl p-5 sm:p-8 md:p-10 mb-4 sm:mb-6 md:mb-8 text-center text-white"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-yellow text-midnight-blue mb-4 sm:mb-5 shadow-lg">
            <SafeIcon icon={FiAward} className="text-4xl sm:text-5xl" />
          </div>
          <p className="text-yellow font-bold uppercase tracking-widest text-xs sm:text-sm mb-3">
            Your assessment is complete
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-3 sm:mb-4">
            Your Results,{participantData.name}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white opacity-90 leading-relaxed max-w-2xl mx-auto">
            Your personalized results are ready. Here are your three dominant Dad-Mind-Fucks and the first steps toward changing these patterns.
          </p>
          <div className="max-w-2xl mx-auto mt-6 sm:mt-8 rounded-2xl border border-yellow border-opacity-60 bg-midnight-blue bg-opacity-40 p-4 sm:p-6 text-left shadow-inner">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-yellow text-midnight-blue flex items-center justify-center">
                <SafeIcon icon={FiMail} className="text-xl" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-yellow mb-2">
                  Your 7-day email series starts now
                </h2>
                <p className="text-sm sm:text-base text-white opacity-95 leading-relaxed">
                  Check your inbox for a deeper look at your top Dad-Mind-Fuck,why it exists,how it affects your life,and what you can do about it.
                </p>
                <div className="flex items-center gap-2 mt-4 text-xs sm:text-sm text-white opacity-90">
                  <SafeIcon icon={FiArrowRight} />
                  <span>Look out for MF.Dads in your inbox over the next 7 days</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.35,duration: 0.6}}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 md:mb-8 font-semibold"
        >
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiMail} className="text-2xl text-md-blue flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-midnight-blue mb-2">
                🧠 What to expect from your email series
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Watch your inbox over the next 7 days for practical guidance on understanding and changing your dominant pattern.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                {emailLessons.map((lesson)=> (
                  <li key={lesson} className="flex items-start gap-2">
                    <span className="text-md-blue font-bold">•</span>
                    <span>{lesson}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        <div className="grid gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
          {topThree.map((result,index)=> (
            <motion.article
              key={result.category}
              initial={{opacity: 0,x: -20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.6 + index * 0.2,duration: 0.6}}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 border-l-4 border-yellow"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-0">
                  <div className="bg-yellow text-midnight-blue font-bold text-lg sm:text-xl md:text-2xl w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center">
                    #{index + 1}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-midnight-blue">
                      {categories[result.category]}
                    </h2>
                    <p className="text-sm sm:text-base text-md-blue font-semibold">
                      Score: {result.score}/20
                    </p>
                  </div>
                </div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-midnight-blue">
                    {Math.round((result.score / 20) * 100)}%
                  </div>
                </div>
              </div>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {categoryDescriptions[result.category]}
              </p>
            </motion.article>
          ))}
        </div>

        <motion.section
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1.2,duration: 0.6}}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8"
        >
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-midnight-blue mb-3 sm:mb-4 md:mb-6 text-center">
            Complete Score Profile
          </h3>
          <ScoreChart scores={allScores} />
        </motion.section>

        <motion.section
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1.4,duration: 0.6}}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8"
        >
          <div className="text-center">
            <SafeIcon icon={FiHeart} className="text-3xl sm:text-4xl text-md-blue mx-auto mb-2 sm:mb-4" />
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-midnight-blue mb-2 sm:mb-4">
              🧠 You're Not Broken,You're Human
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
              These patterns developed to protect you,but they might not be serving you anymore. Now that you’re aware of them,you can choose how to respond.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 mt-4 sm:mt-6 md:mt-8">
            {[
              ['Step 1: Awareness','Notice when patterns show up'],
              ['Step 2: Pause','Take a breath before reacting'],
              ['Step 3: Choose','Respond from your values']
            ].map(([title,text])=> (
              <div key={title} className="text-center p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg">
                <h4 className="text-xs sm:text-sm md:text-base font-bold text-midnight-blue mb-1 sm:mb-2">
                  {title}
                </h4>
                <p className="text-xs text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1.6,duration: 0.6}}
          className="bg-midnight-blue rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 text-center text-white"
        >
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-4">
            Ready to Break Free?
          </h3>
          <p className="text-sm sm:text-base md:text-lg mb-4 sm:mb-6 md:mb-8 opacity-90">
            Let's talk about how you can transform these patterns and become the dad you want to be.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleBookCall}
              className="bg-yellow text-midnight-blue font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <SafeIcon icon={FiCalendar} />
              <span>Book Free Call</span>
            </motion.button>
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleRetakeQuiz}
              className="border-2 border-white text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 hover:bg-white hover:text-midnight-blue flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Retake Quiz</span>
            </motion.button>
          </div>
        </motion.section>

        <div className="text-center mt-4 sm:mt-6 md:mt-8 text-white opacity-75">
          <p className="text-xs sm:text-sm">
            Thank you for investing in yourself,{participantData.name}. Your family is lucky to have you.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultsPage;